import { useCallback, useRef, useState } from "react";
import type { RefObject } from "react";
import { formatName } from "@tellescope/utils";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";

type User = TellescopeModelFromName<"users">;

export type MentionTarget = { type: "user"; user: User } | { type: "group"; name: string; userIds: string[] };

export interface MentionState {
  isOpen: boolean;
  query: string;
  tokenStart: number;
}

const CLOSED_STATE: MentionState = { isOpen: false, query: "", tokenStart: -1 };

const findMentionToken = (text: string, cursorPos: number): { tokenStart: number; query: string } | null => {
  const textBeforeCursor = text.slice(0, cursorPos);
  const match = /@([^\s@]*)$/.exec(textBeforeCursor);
  if (!match) return null;
  return { tokenStart: match.index, query: match[1] ?? "" };
};

const mentionKey = (target: MentionTarget) =>
  target.type === "user" ? `user:${target.user.id}` : `group:${target.name}`;

export interface UseMentionsResult {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  mentionState: MentionState;
  pendingMentions: MentionTarget[];
  handleMessageChange: (value: string) => void;
  onMentionSelect: (target: MentionTarget, currentMessage: string) => void;
  onMentionRemove: (target: MentionTarget) => void;
  onMentionDismiss: () => void;
  buildMentionPayload: () => { mentions: string[] };
  reset: () => void;
}

export const useMentions = (): UseMentionsResult => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionState, setMentionState] = useState<MentionState>(CLOSED_STATE);
  const [pendingMentions, setPendingMentions] = useState<MentionTarget[]>([]);

  const handleMessageChange = useCallback((value: string) => {
    const cursorPos = textareaRef.current?.selectionStart ?? value.length;
    const token = findMentionToken(value, cursorPos);
    if (token) {
      setMentionState({ isOpen: true, query: token.query, tokenStart: token.tokenStart });
    } else {
      setMentionState(CLOSED_STATE);
    }
  }, []);

  const onMentionSelect = useCallback((target: MentionTarget, currentMessage: string) => {
    const cursorPos = textareaRef.current?.selectionStart ?? currentMessage.length;
    const token = findMentionToken(currentMessage, cursorPos);
    if (!token) {
      setMentionState(CLOSED_STATE);
      return;
    }

    const displayName = target.type === "user" ? formatName(target.user) : target.name;
    const mentionText = `@${displayName} `;
    const newMessage = currentMessage.slice(0, token.tokenStart) + mentionText + currentMessage.slice(cursorPos);

    const textarea = textareaRef.current;
    if (textarea) {
      const nativeSet = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
      nativeSet?.call(textarea, newMessage);
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
      textarea.dispatchEvent(new Event("change", { bubbles: true }));
    }
    setMentionState(CLOSED_STATE);

    const key = mentionKey(target);
    setPendingMentions((prev) => {
      if (prev.some((t) => mentionKey(t) === key)) return prev;
      return [...prev, target];
    });

    const newCursorPos = token.tokenStart + mentionText.length;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    });
  }, []);

  const onMentionRemove = useCallback((target: MentionTarget) => {
    const key = mentionKey(target);
    setPendingMentions((prev) => prev.filter((t) => mentionKey(t) !== key));

    // Also strip the @Name token from the textarea value
    const textarea = textareaRef.current;
    if (!textarea) return;
    const displayName = target.type === "user" ? formatName(target.user) : target.name;
    const pattern = new RegExp(`@${displayName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?`, "g");
    const newValue = textarea.value.replace(pattern, "");
    if (newValue === textarea.value) return;
    const nativeSet = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
    nativeSet?.call(textarea, newValue);
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
    textarea.dispatchEvent(new Event("change", { bubbles: true }));
  }, []);

  const onMentionDismiss = useCallback(() => setMentionState(CLOSED_STATE), []);

  const buildMentionPayload = useCallback((): { mentions: string[] } => {
    const userIds = new Set<string>();
    for (const target of pendingMentions) {
      if (target.type === "user") {
        userIds.add(target.user.id);
      } else {
        target.userIds.forEach((id) => userIds.add(id));
      }
    }
    return { mentions: Array.from(userIds) };
  }, [pendingMentions]);

  const reset = useCallback(() => {
    setPendingMentions([]);
    setMentionState(CLOSED_STATE);
  }, []);

  return {
    textareaRef,
    mentionState,
    pendingMentions,
    handleMessageChange,
    onMentionSelect,
    onMentionRemove,
    onMentionDismiss,
    buildMentionPayload,
    reset,
  };
};
